<template>
    <Loader v-if="loadingTemplatePage" />
    <component :is="currentTemplate" />
     <!-- <h1>Test</h1> -->
     <!-- <ProjectManagement /> -->
</template>

<script>
import ProjectManagement from "@/layouts/ProjectManagement.vue";
import ServiceProcurement from "@/layouts/ServiceProcurement.vue";
import { mapActions, mapState } from "vuex";
import PropertyManagement from "@/layouts/PropertyManagement.vue";
import IncidentManagement from "@/layouts/IncidentManagement.vue";
import LoyaltyManagement from "@/layouts/LoyaltyManagement.vue";
import Loader from "@/components/Loader.vue";
import { sectionLoaders } from "@/sectionRegistry.js";
import { announcePageRevealed, beginPageLoad } from "@/utils/aos";

// Ceiling on waiting for the first screen's artwork. The page is worth showing
// without a slow image; it is not worth withholding behind one.
const ARTWORK_TIMEOUT_MS = 2500;

export default {
    name: "TemplateRenderer",
    components: {
        ProjectManagement,
        ServiceProcurement,
        PropertyManagement,
        IncidentManagement,
        LoyaltyManagement,
        Loader
    },
    computed: {
        // sectionList is what sectionChunksReady() reads to know which chunks
        // this page needs; without it here `this.sectionList` is undefined and
        // the preload silently becomes a no-op.
        ...mapState(["templateName", "projectId", "sectionList"]),
        currentTemplate() {
            const templates = {
                ProjectManagement: "ProjectManagement",
                ServiceProcurement: "ServiceProcurement",
                PropertyManagement: "PropertyManagement",
                IncidentManagement: "IncidentManagement",
                LoyaltyManagement: "LoyaltyManagement"
            };
            

            return templates[this.templateName] || null;
        },
    },
    data() {
        return {
            loadingTemplatePage: true,
            // Handle for the artwork ceiling, cleared on unmount so navigating
            // away mid-load cannot fire a timer into a destroyed instance.
            artworkTimer: null
        }
    },
    async created() {
        // The overlay is up from here. The hero of whichever layout this
        // resolves to mounts later - `<component :is>` cannot resolve until the
        // store has templateName - so this is what tells that hero it is
        // arriving behind a loader rather than onto a visible page.
        beginPageLoad();
        if (this.$route.query.reload) {
            this.$router.push(`/solutions/${this.$route.params.ProjectId}`);
        } else {
            // Always ask, rather than skipping when state.projectId already
            // matches. loadTemplateConfig commits setProjectId up front, before
            // it has fetched anything, so a matching id proves only that a load
            // has STARTED - and the solutions list now starts one speculatively
            // on hover. Taking the shortcut against an in-flight prefetch
            // rendered the page with the previous project's templateName and
            // sections. The action dedupes by id and returns from cache, so
            // asking every time costs nothing when the work is already done.
            this.loadingTemplatePage = true;
            await this.$store.dispatch('loadTemplateConfig', this.$route.params.ProjectId);
            // The config arriving is not the page being ready. Dropping the
            // overlay here handed over a layout that had not rendered yet, so
            // the reader watched the hero assemble itself. Hold until the
            // template has mounted and its first screen has something to paint.
            await this.pageReady();
            this.loadingTemplatePage = false;
            this.$nextTick(announcePageRevealed);
        }
    },
    mounted() {
        $(function () {
            'use strict';
            $(".loader").delay(200).fadeOut("slow");
            $("#overlayer").delay(200).fadeOut("slow");
        });
    },
    beforeUnmount() {
        clearTimeout(this.artworkTimer);
        this.artworkTimer = null;
    },
    methods: {
        /**
         * Resolves once the chosen template is on screen rather than merely
         * decided: its components mounted, the artwork of the first screen
         * decoded, and one frame given to paint it.
         *
         * @returns {Promise<void>}
         */
        async pageReady() {
            // Every section is its own lazy chunk, so a layout that has
            // "rendered" is at this point a Header, a placeholder per section
            // and a Footer. Waiting only for $nextTick lifted the overlay onto
            // exactly that - the bare footer flash. Pull the chunks first, and
            // the sections then render in the flush below rather than trickling
            // in afterwards.
            await this.sectionChunksReady();
            // Lets `<component :is>` resolve and every child's mounted() run.
            // Twice: the first flush resolves the async components now that
            // their modules are in hand, the second renders what they returned.
            await this.$nextTick();
            await this.$nextTick();
            await this.heroArtworkReady();
            // One more frame, so the overlay lifts on a painted page rather
            // than on the frame that is still painting it.
            await new Promise((resolve) => requestAnimationFrame(() => resolve()));
        },

        /**
         * Loads the chunk behind every section this page is about to render.
         *
         * Resolves even if one fails: a section that cannot load is a broken
         * section, not a reason to hold the whole page behind the overlay —
         * Vue will render its error/empty state and the rest still shows.
         *
         * @returns {Promise<void>}
         */
        async sectionChunksReady() {
            const names = (this.sectionList || [])
                .map((block) => block && block.name)
                .filter(Boolean);
            // De-duplicated: a template may use the same section twice, and
            // there is no point asking webpack for the same chunk again.
            const unique = [...new Set(names)];

            await Promise.all(unique.map((name) => {
                const load = sectionLoaders[name];
                if (typeof load !== 'function') return null;
                return load().catch(() => null);
            }));
        },

        /**
         * Waits for the images of the first screen to finish loading.
         *
         * Only what the reader is about to look at - everything below the fold
         * can arrive in its own time, and holding for it would trade one kind
         * of lag for a longer one. Never rejects, and always resolves: a slow
         * CDN must not strand the page behind the overlay.
         *
         * @returns {Promise<void>}
         */
        heroArtworkReady() {
            return new Promise((resolve) => {
                let settled = false;
                const done = () => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(this.artworkTimer);
                    this.artworkTimer = null;
                    resolve();
                };

                this.artworkTimer = setTimeout(done, ARTWORK_TIMEOUT_MS);

                const fold = (window.innerHeight || 800) * 1.15;
                const pending = Array.from(document.images).filter((img) => {
                    if (img.complete) return false;
                    const rect = img.getBoundingClientRect();
                    // Zero-sized images are display:none or not laid out; either
                    // way they are not part of what is about to be looked at.
                    return rect.width > 0 && rect.height > 0 && rect.top < fold;
                });

                if (!pending.length) { done(); return; }

                let outstanding = pending.length;
                const settleOne = () => { if (--outstanding <= 0) done(); };
                pending.forEach((img) => {
                    img.addEventListener('load', settleOne, { once: true });
                    img.addEventListener('error', settleOne, { once: true });
                });
            });
        }
    }
};
</script>

<style>
</style>